import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2,
  CheckCircle2,
  Briefcase,
  MapPin,
  Upload,
  X,
  FileText,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Textarea } from "@/components/ui/textarea";
import { formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEKLY_OPTIONS = [1, 2, 3, 4, 5, 6];
const MAX_CV_BYTES = 20 * 1024 * 1024;

export default function ApplyPage() {
  const { slug } = useParams();
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    last_name: "",
    first_name: "",
    email: "",
    phone: "",
    hospitality_experience: "",
    weekly_commitment: "",
    student_under_25: "",
    birth_date: "",
    social_link: "",
    about_me: "",
  });
  const [cv, setCv] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/public/positions/${slug}`
        );
        if (!cancelled) setPosition(data);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const upd = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: typeof e === "string" ? e : e.target.value,
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    const required = [
      ["last_name", "Vezetéknév"],
      ["first_name", "Keresztnév"],
      ["email", "E-mail"],
      ["phone", "Telefon"],
      ["hospitality_experience", "Vendéglátós tapasztalat"],
      ["weekly_commitment", "Heti vállalás"],
      ["student_under_25", "25 év alatti diák"],
      ["birth_date", "Születési idő"],
    ];
    for (const [k, label] of required) {
      if (!form[k] || String(form[k]).trim() === "") {
        setErr(`A "${label}" mező kötelező.`);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      setErr("Érvénytelen e-mail cím.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("last_name", form.last_name.trim());
      fd.append("first_name", form.first_name.trim());
      fd.append("email", form.email.trim());
      fd.append("phone", form.phone.trim());
      fd.append("hospitality_experience", form.hospitality_experience);
      fd.append("weekly_commitment", form.weekly_commitment);
      fd.append("student_under_25", form.student_under_25);
      fd.append("birth_date", form.birth_date);
      if (form.social_link.trim())
        fd.append("social_link", form.social_link.trim());
      if (form.about_me.trim()) fd.append("about_me", form.about_me.trim());
      if (cv) fd.append("cv", cv);

      await axios.post(
        `${API_BASE}/public/positions/${slug}/applications`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setErr(
        formatApiError(error?.response?.data?.detail) ||
          "Hiba történt a beküldés során. Próbáld újra."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-32 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Betöltés…
        </div>
      </Shell>
    );
  }
  if (notFound) {
    return (
      <Shell>
        <NotFoundCard />
      </Shell>
    );
  }
  if (submitted) {
    return (
      <Shell>
        <SuccessCard positionName={position.name} />
      </Shell>
    );
  }

  return (
    <Shell>
      <Hero position={position} />

      <form
        onSubmit={handleSubmit}
        data-testid="apply-form"
        className="overflow-hidden rounded-3xl border border-border/70 bg-[hsl(var(--sipx-elevated))] shadow-[0_1px_2px_0_rgba(0,0,0,0.04),0_24px_60px_-32px_rgba(0,0,0,0.18)] divide-y divide-border/70"
      >
        {/* Section 01 — Personal info */}
        <Section index="01" title="Személyes adatok" subtitle="Hogyan érhetünk el?">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Vezetéknév" required testId="field-last-name">
              <Input
                value={form.last_name}
                onChange={upd("last_name")}
                autoComplete="family-name"
                maxLength={120}
                className="h-11"
              />
            </Field>
            <Field label="Keresztnév" required testId="field-first-name">
              <Input
                value={form.first_name}
                onChange={upd("first_name")}
                autoComplete="given-name"
                maxLength={120}
                className="h-11"
              />
            </Field>
            <Field label="E-mail" required testId="field-email">
              <Input
                type="email"
                value={form.email}
                onChange={upd("email")}
                autoComplete="email"
                maxLength={200}
                className="h-11"
                placeholder="pelda@email.hu"
              />
            </Field>
            <Field label="Telefon" required testId="field-phone">
              <Input
                type="tel"
                value={form.phone}
                onChange={upd("phone")}
                autoComplete="tel"
                placeholder="+36 30 ..."
                maxLength={40}
                className="h-11"
              />
            </Field>
            <Field
              label="Születési idő"
              required
              testId="field-birth-date"
              className="sm:col-span-2"
            >
              <DateInput
                value={form.birth_date}
                onChange={upd("birth_date")}
                className="h-11"
                maxDate={new Date()}
              />
            </Field>
          </div>
        </Section>

        {/* Section 02 — Experience & commitment */}
        <Section
          index="02"
          title="Tapasztalat & vállalás"
          subtitle="Néhány kérdés, hogy jobban megismerjük a profilodat."
        >
          <div className="space-y-6">
            <Field
              label="Van vendéglátós tapasztalatod?"
              required
              testId="field-hospitality"
            >
              <Segmented
                value={form.hospitality_experience}
                onChange={upd("hospitality_experience")}
                options={[
                  { value: "true", label: "Igen, van" },
                  { value: "false", label: "Nincs" },
                ]}
              />
            </Field>

            <Field
              label="Hány napot tudsz vállalni hetente?"
              required
              testId="field-weekly"
            >
              <ChipGroup
                value={form.weekly_commitment}
                onChange={upd("weekly_commitment")}
                options={WEEKLY_OPTIONS.map((n) => ({
                  value: String(n),
                  label: `${n} nap`,
                }))}
              />
            </Field>

            <Field
              label="25 év alatti diák vagy?"
              required
              testId="field-student"
            >
              <Segmented
                value={form.student_under_25}
                onChange={upd("student_under_25")}
                options={[
                  { value: "true", label: "Igen" },
                  { value: "false", label: "Nem" },
                ]}
              />
            </Field>
          </div>
        </Section>

        {/* Section 03 — Intro & documents */}
        <Section
          index="03"
          title="Bemutatkozás & dokumentumok"
          subtitle="Mutasd meg magad — adj nekünk egy benyomást."
        >
          <div className="space-y-5">
            <Field label="Social link" testId="field-social">
              <Input
                type="url"
                value={form.social_link}
                onChange={upd("social_link")}
                placeholder="Instagram, LinkedIn, TikTok…"
                maxLength={500}
                className="h-11"
              />
            </Field>

            <Field label="Magamról" testId="field-about">
              <Textarea
                value={form.about_me}
                onChange={upd("about_me")}
                rows={5}
                placeholder="Mesélj röviden magadról, az eddigi tapasztalataidról és arról, hogy miért jelentkezel."
                maxLength={4000}
                className="min-h-[140px] resize-none"
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Maximum 4000 karakter.
              </p>
            </Field>

            <Field label="Önéletrajz (CV)" testId="field-cv">
              <Dropzone file={cv} onChange={setCv} onError={setErr} />
            </Field>
          </div>
        </Section>

        {err && (
          <div
            data-testid="apply-error"
            className="border-t border-destructive/30 bg-destructive/5 px-5 py-3 text-[12.5px] text-destructive sm:px-7"
          >
            {err}
          </div>
        )}
      </form>

      <div className="sticky bottom-4 z-10 mt-5">
        <div className="rounded-2xl border border-border/70 bg-[hsl(var(--sipx-elevated))]/95 px-4 py-3 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.18)] backdrop-blur-xl supports-[backdrop-filter]:bg-[hsl(var(--sipx-elevated))]/80">
          <div className="flex items-center justify-between gap-3">
            <p className="hidden text-[11.5px] text-muted-foreground sm:block">
              Adataidat bizalmasan kezeljük.
            </p>
            <Button
              type="submit"
              form="apply-form"
              disabled={submitting}
              data-testid="apply-submit"
              onClick={handleSubmit}
              className="group h-10 w-full gap-1.5 rounded-xl px-5 text-[13px] sm:w-auto sm:min-w-[200px]"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Beküldés…
                </>
              ) : (
                <>
                  Jelentkezés beküldése
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Shell>
  );
}

/* -------------------------- Layout -------------------------- */

function Shell({ children }) {
  return (
    <div className="relative min-h-screen w-full bg-neutral-50 text-foreground antialiased dark:bg-neutral-950">
      {/* Subtle radial accent */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[380px]"
        style={{
          background:
            "radial-gradient(60% 100% at 50% 0%, hsl(var(--foreground) / 0.04), transparent 70%)",
        }}
      />
      <main className="relative mx-auto w-full max-w-[640px] px-4 pb-28 pt-10 sm:pt-14">
        {children}
      </main>
      <footer className="mx-auto w-full max-w-[640px] px-4 pb-8 text-center text-[11px] text-muted-foreground">
        © SIPX Group
      </footer>
    </div>
  );
}

function Hero({ position }) {
  return (
    <section data-testid="position-header" className="mb-5 space-y-3 px-1">
      <div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground">
        <span className="grid h-5 w-5 place-items-center rounded-md bg-foreground text-background">
          <span className="text-[9px] font-bold tracking-tighter">sx</span>
        </span>
        <span className="uppercase tracking-[0.16em]">sipx · karrier</span>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-400">
          <span className="h-1 w-1 rounded-full bg-emerald-500" />
          Aktívan toborzunk
        </span>
      </div>
      <h1 className="text-[28px] font-semibold leading-tight tracking-tight sm:text-[34px]">
        {position.name}
      </h1>
      {(position.location || position.description) && (
        <div className="space-y-2">
          {position.location && (
            <p className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
              <MapPin className="h-3 w-3" strokeWidth={1.75} />
              {position.location}
            </p>
          )}
          {position.description && (
            <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-muted-foreground">
              {position.description}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function Section({ index, title, subtitle, children }) {
  return (
    <section className="px-5 py-6 sm:px-7 sm:py-7">
      <header className="mb-5 flex items-start gap-3">
        <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-foreground text-[10px] font-semibold text-background">
          {index}
        </span>
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold tracking-tight">{title}</h2>
          {subtitle && (
            <p className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
              {subtitle}
            </p>
          )}
        </div>
      </header>
      {children}
    </section>
  );
}

/* -------------------------- Form primitives -------------------------- */

function Field({ label, required, children, testId, className }) {
  return (
    <label
      className={cn("group/field block space-y-2", className)}
      data-testid={testId}
    >
      <span className="flex items-baseline gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors group-focus-within/field:text-foreground">
        {label}
        {required && (
          <span
            className="text-rose-500/80"
            aria-label="kötelező"
            title="Kötelező mező"
          >
            *
          </span>
        )}
      </span>
      <div className="transition-shadow group-focus-within/field:[&_input]:ring-2 group-focus-within/field:[&_input]:ring-foreground/30 group-focus-within/field:[&_textarea]:ring-2 group-focus-within/field:[&_textarea]:ring-foreground/30">
        {children}
      </div>
    </label>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex w-full rounded-xl border border-border bg-background/60 p-1 shadow-inner backdrop-blur-sm sm:w-auto">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`segmented-${opt.value}`}
            onClick={() => onChange(opt.value)}
            className={cn(
              "relative flex-1 rounded-lg px-5 py-2 text-sm font-medium transition-all duration-200 sm:flex-none",
              active
                ? "bg-foreground text-background shadow-md ring-1 ring-white/10"
                : "text-muted-foreground hover:bg-foreground/[0.04] hover:text-foreground"
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ChipGroup({ value, onChange, options }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`chip-${opt.value}`}
            onClick={() => onChange(opt.value)}
            className={cn(
              "rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200",
              active
                ? "border-foreground bg-foreground text-background shadow-md ring-1 ring-white/10"
                : "border-border bg-background/60 text-muted-foreground backdrop-blur-sm hover:-translate-y-px hover:border-foreground/40 hover:text-foreground"
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Dropzone({ file, onChange, onError }) {
  const inputRef = useRef(null);
  const [isOver, setIsOver] = useState(false);

  const accept = (f) => {
    if (!f) return;
    if (f.size > MAX_CV_BYTES) {
      onError(`Az önéletrajz mérete túl nagy (max. ${MAX_CV_BYTES / (1024 * 1024)} MB).`);
      return;
    }
    onError("");
    onChange(f);
  };

  if (file) {
    return (
      <div
        data-testid="cv-selected"
        className="flex items-center justify-between gap-3 rounded-xl border border-border bg-background p-4"
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-foreground/[0.06] text-foreground">
            <FileText className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{file.name}</p>
            <p className="text-[11px] text-muted-foreground">
              {(file.size / 1024).toFixed(1)} KB · feltöltésre kész
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label="Fájl eltávolítása"
          className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsOver(false);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          setIsOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) accept(f);
        }}
        className={cn(
          "group flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-all",
          isOver
            ? "border-foreground bg-foreground/[0.04]"
            : "border-border bg-background hover:border-foreground/40"
        )}
      >
        <div
          className={cn(
            "grid h-12 w-12 place-items-center rounded-full bg-foreground/[0.05] transition-transform",
            isOver ? "scale-110" : "group-hover:scale-105"
          )}
        >
          <Upload className="h-5 w-5" strokeWidth={1.75} />
        </div>
        <p className="text-sm font-medium">
          {isOver ? "Engedd el a fájlt" : "Húzd ide a CV-d, vagy katt a feltöltéshez"}
        </p>
        <p className="text-[11px] text-muted-foreground">
          PDF · DOC · DOCX · JPG · PNG (max. 20 MB)
        </p>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
        className="hidden"
        onChange={(e) => accept(e.target.files?.[0])}
      />
    </div>
  );
}

/* -------------------------- States -------------------------- */

function SuccessCard({ positionName }) {
  return (
    <div
      data-testid="application-success"
      className="relative overflow-hidden rounded-3xl border border-border bg-[hsl(var(--sipx-elevated))] px-6 py-16 text-center"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-emerald-500/10 to-transparent"
      />
      <div className="relative">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-500/10 text-emerald-600">
          <CheckCircle2 className="h-8 w-8" strokeWidth={1.5} />
        </div>
        <h1 className="mt-6 text-3xl font-semibold tracking-tight sm:text-4xl">
          Köszönjük a jelentkezésed!
        </h1>
        <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-muted-foreground">
          Megkaptuk a jelentkezésed a(z){" "}
          <span className="font-semibold text-foreground">{positionName}</span>{" "}
          pozícióra. A kollégáink hamarosan felveszik veled a kapcsolatot.
        </p>
      </div>
    </div>
  );
}

function NotFoundCard() {
  return (
    <div className="flex flex-col items-center gap-4 rounded-3xl border border-border bg-[hsl(var(--sipx-elevated))] px-6 py-16 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-full border border-border text-muted-foreground/80">
        <Briefcase className="h-6 w-6" strokeWidth={1.5} />
      </div>
      <h1 className="text-2xl font-semibold tracking-tight">
        Ez a pozíció már nem elérhető
      </h1>
      <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
        A jelentkezési link lejárt vagy a pozíció időközben deaktiválásra
        került. Köszönjük az érdeklődést!
      </p>
    </div>
  );
}
